import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Heart, MessageSquare, Building2, Sparkles, ShieldCheck, Phone, Star, Settings, Share2, Copy as CopyIcon, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtFJD, formatApiError } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import JVCard from "@/components/JVCard";
import DealFeed from "@/components/DealFeed";
import SavedSearchesList from "@/components/SavedSearchesList";
import { SERVICE_BY_ID } from "@/lib/services";

const statusColors = {
    pending: "bg-coral/15 text-coral",
    approved: "bg-fiji-green/15 text-fiji-green",
    rejected: "bg-destructive/15 text-destructive",
    info_requested: "bg-turquoise/15 text-ocean dark:text-turquoise",
};

export default function Dashboard() {
    const { user } = useAuth();
    const [myListings, setMyListings] = useState([]);
    const [myJvs, setMyJvs] = useState([]);
    const [favs, setFavs] = useState({ listings: [], jvs: [] });
    const [threads, setThreads] = useState([]);
    const [referral, setReferral] = useState(null);

    useEffect(() => {
        api.get("/my/listings").then(({ data }) => setMyListings(data.items || []));
        api.get("/my/jvs").then(({ data }) => setMyJvs(data.items || []));
        api.get("/favorites").then(({ data }) => setFavs(data));
        api.get("/threads").then(({ data }) => setThreads(data.items || []));
        api.get("/referral/me").then(({ data }) => setReferral(data)).catch(() => {});
    }, []);

    const referralLink = referral?.referral_code
        ? `${window.location.origin}/register?ref=${referral.referral_code}`
        : "";

    const copyReferralLink = () => {
        if (!referralLink) return;
        navigator.clipboard.writeText(referralLink).then(() => {
            toast.success("Referral link copied — share it with landowners and investors.");
        });
    };

    const isSeller = user?.roles?.some(r => ["seller", "agent", "admin"].includes(r));
    const activeServices = (user?.services || []).map((id) => SERVICE_BY_ID[id]).filter(Boolean);
    const has = (id) => (user?.services || []).includes(id);

    const upgradeToPremium = async (jvId) => {
        try {
            const { data } = await api.post("/payments/checkout/session", {
                package_id: "jv_premium",
                jv_id: jvId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
        }
    };

    const upgradeToBoost = async (jvId) => {
        try {
            const { data } = await api.post("/payments/checkout/session", {
                package_id: "jv_boost",
                jv_id: jvId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
        }
    };

    return (
        <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-1">Dashboard</div>
                    <h1 className="font-display text-4xl font-bold">Bula, {user?.full_name?.split(" ")[0] || "there"} 🌺</h1>
                    <p className="text-muted-foreground font-app mt-1">Manage your Fiji land portfolio, Joint Ventures and conversations.</p>
                </div>
                {!user?.phone_verified && (
                    <Link to="/verify-phone">
                        <Card className="p-3 px-4 border-coral bg-coral/5 flex items-center gap-2 hover:bg-coral/10 cursor-pointer">
                            <Phone className="h-4 w-4 text-coral" />
                            <span className="text-sm font-app font-medium">Verify your phone →</span>
                        </Card>
                    </Link>
                )}
            </div>

            {/* Active Service Lanes — adapts dashboard to what the user signed up for */}
            <Card className="p-5 mb-6 bg-gradient-to-r from-ocean/5 to-fiji-green/5 border-ocean/20 dark:from-turquoise/5 dark:to-fiji-green/10 dark:border-turquoise/20" data-testid="dashboard-services-strip">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-[0.22em] text-fiji-green dark:text-turquoise font-app font-semibold mb-2">Your active lanes</div>
                        {activeServices.length === 0 ? (
                            <p className="font-app text-sm text-muted-foreground">
                                You haven't picked any service lanes yet. Visit your profile to choose what brings you to the marketplace.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {activeServices.map((s) => {
                                    const Icon = s.icon;
                                    return (
                                        <Badge
                                            key={s.id}
                                            data-testid={`active-lane-${s.id}`}
                                            className="bg-card border border-border text-foreground font-app font-medium px-3 py-1.5"
                                        >
                                            <Icon className={`h-3.5 w-3.5 mr-1.5 ${s.accent}`} />
                                            {s.short}
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <Button asChild variant="outline" size="sm" data-testid="manage-lanes-btn" className="font-app">
                        <Link to="/profile"><Settings className="h-4 w-4 mr-1" /> Manage lanes</Link>
                    </Button>
                </div>
            </Card>

            {/* Referral program card — every user gets a unique link */}
            <Card className="p-5 mb-6 bg-gradient-to-r from-coral/5 to-fiji-green/5 border-coral/30 dark:from-coral/10 dark:to-fiji-green/10" data-testid="referral-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Share2 className="h-4 w-4 text-coral" />
                            <div className="text-xs uppercase tracking-[0.22em] text-coral font-app font-semibold">Refer a landowner</div>
                        </div>
                        <h3 className="font-display text-lg font-bold mb-1">Share your link, earn on every closed Joint Venture.</h3>
                        <p className="font-app text-sm text-muted-foreground mb-3">
                            Anyone who registers via your link is tagged to you. When their deal closes, our admin team can mark you as the referrer and route a slice of the fee to your account.
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <code data-testid="referral-link" className="text-xs font-mono px-3 py-2 rounded-md bg-card border border-border break-all flex-1 min-w-[180px]">
                                {referralLink || "Generating…"}
                            </code>
                            <Button onClick={copyReferralLink} data-testid="copy-referral-btn" disabled={!referralLink} variant="outline" size="sm" className="font-app">
                                <CopyIcon className="h-3.5 w-3.5 mr-1.5" /> Copy
                            </Button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 min-w-[220px]" data-testid="referral-stats">
                        <div className="text-center p-2.5 rounded-md bg-card border border-border">
                            <div className="font-display text-xl font-bold tabular-nums" data-testid="ref-users-count">{referral?.referred_users_count ?? 0}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Referred users</div>
                        </div>
                        <div className="text-center p-2.5 rounded-md bg-card border border-border">
                            <div className="font-display text-xl font-bold tabular-nums text-fiji-green" data-testid="ref-earnings">{fmtFJD(referral?.referrer_earnings_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Earnings paid</div>
                        </div>
                        <div className="text-center p-2.5 rounded-md bg-card border border-border">
                            <div className="font-display text-xl font-bold tabular-nums text-coral" data-testid="ref-receivable">{fmtFJD(referral?.referrer_receivable_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Receivable</div>
                        </div>
                        <div className="text-center p-2.5 rounded-md bg-card border border-border">
                            <div className="font-display text-xl font-bold tabular-nums text-ocean dark:text-turquoise" data-testid="ref-future">{fmtFJD(referral?.referrer_future_fjd || 0)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">In pipeline</div>
                        </div>
                    </div>
                </div>
                {referral?.deals?.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-border">
                        <div className="flex items-center gap-2 mb-2">
                            <Banknote className="h-3.5 w-3.5 text-coral" />
                            <h4 className="font-display text-sm font-semibold">My referred deals ({referral.deals.length})</h4>
                        </div>
                        <div className="overflow-x-auto" data-testid="referral-deals-table">
                            <table className="w-full text-sm font-app">
                                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr className="border-b border-border">
                                        <th className="text-left py-2 pr-3">Deal</th>
                                        <th className="text-left py-2 pr-3">Type</th>
                                        <th className="text-left py-2 pr-3">Region</th>
                                        <th className="text-left py-2 pr-3">Status</th>
                                        <th className="text-right py-2">Your payout</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {referral.deals.map((d) => (
                                        <tr key={d.id} data-testid={`referral-deal-row-${d.id}`} className="hover:bg-secondary/30 transition-colors">
                                            <td className="py-2.5 pr-3 max-w-[260px] truncate font-medium">{d.title}</td>
                                            <td className="py-2.5 pr-3 uppercase text-[11px] text-muted-foreground">{d.asset_type || "—"}</td>
                                            <td className="py-2.5 pr-3 text-muted-foreground">{d.region || "—"}</td>
                                            <td className="py-2.5 pr-3">
                                                <span className={
                                                    d.status === "paid" ? "inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold bg-fiji-green/15 text-fiji-green" :
                                                    d.status === "closed" ? "inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold bg-coral/15 text-coral" :
                                                    d.status === "in_diligence" ? "inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold bg-ocean/15 text-ocean dark:bg-turquoise/15 dark:text-turquoise" :
                                                    d.status === "dead" ? "inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold bg-muted text-muted-foreground" :
                                                    "inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold bg-secondary text-foreground"
                                                }>
                                                    {String(d.status || "").replace("_", " ") || "—"}
                                                </span>
                                            </td>
                                            <td className="py-2.5 text-right tabular-nums font-semibold">{fmtFJD(d.referrer_payout_fjd || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Card>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="p-5">
                    <Building2 className="h-5 w-5 text-ocean mb-2" />
                    <div className="font-display text-3xl font-bold">{myListings.length}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">My listings</div>
                </Card>
                <Card className="p-5">
                    <Sparkles className="h-5 w-5 text-coral mb-2" />
                    <div className="font-display text-3xl font-bold">{myJvs.length}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">My Joint Ventures</div>
                </Card>
                <Card className="p-5">
                    <Heart className="h-5 w-5 text-fiji-green mb-2" />
                    <div className="font-display text-3xl font-bold">{favs.listings.length + favs.jvs.length}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Favorites</div>
                </Card>
                <Card className="p-5">
                    <MessageSquare className="h-5 w-5 text-turquoise mb-2" />
                    <div className="font-display text-3xl font-bold">{threads.length}</div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Conversations</div>
                </Card>
            </div>

            {/* Personalized deal feed (auto-hides when no matches) */}
            <DealFeed limit={6} />

            {/* Saved searches widget — auto-shows empty-state CTA when none yet */}
            <SavedSearchesList />

            {(isSeller || has("landowner") || has("agent")) && (
                <div className="flex flex-wrap gap-3 mb-8">
                    {(has("landowner") || has("agent") || isSeller) && (
                        <Button asChild data-testid="dashboard-new-listing" className="bg-ocean-green text-white">
                            <Link to="/dashboard/listings/new"><Plus className="h-4 w-4 mr-1" /> New listing</Link>
                        </Button>
                    )}
                    {(has("agent") || (isSeller && has("landowner"))) && (
                        <Button asChild variant="outline" data-testid="dashboard-bulk-listings">
                            <Link to="/dashboard/listings/bulk"><Plus className="h-4 w-4 mr-1" /> Bulk upload</Link>
                        </Button>
                    )}
                    {(has("landowner") || has("agent") || isSeller) && (
                        <Button asChild variant="outline" data-testid="dashboard-new-jv">
                            <Link to="/jv/submit"><Plus className="h-4 w-4 mr-1" /> Submit Joint Venture</Link>
                        </Button>
                    )}
                    {has("business_seller") && (
                        <Button asChild data-testid="dashboard-new-business" className="bg-coral text-white">
                            <Link to="/dashboard/businesses/new"><Plus className="h-4 w-4 mr-1" /> List a business</Link>
                        </Button>
                    )}
                    {has("business_seller") && (
                        <Button asChild variant="outline" data-testid="dashboard-nda-inbox">
                            <Link to="/dashboard/nda-inbox"><MessageSquare className="h-4 w-4 mr-1" /> NDA Inbox</Link>
                        </Button>
                    )}
                </div>
            )}

            <Tabs defaultValue="listings">
                <TabsList className="font-app">
                    <TabsTrigger value="listings" data-testid="tab-listings">My listings</TabsTrigger>
                    <TabsTrigger value="jvs" data-testid="tab-jvs">My Joint Ventures</TabsTrigger>
                    <TabsTrigger value="favorites" data-testid="tab-favorites">Favorites</TabsTrigger>
                </TabsList>

                <TabsContent value="listings" className="mt-6">
                    {myListings.length === 0 ? (
                        <EmptyState text="You haven't created any listings yet." cta="Create your first listing" to="/dashboard/listings/new" />
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {myListings.map((l) => <PropertyCard key={l.id} listing={l} />)}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="jvs" className="mt-6">
                    {myJvs.length === 0 ? (
                        <EmptyState text="No Joint Venture submissions yet." cta="Submit a Joint Venture" to="/jv/submit" />
                    ) : (
                        <div className="grid md:grid-cols-2 gap-4">
                            {myJvs.map((jv) => (
                                <Card key={jv.id} className="p-6 relative">
                                    {jv.is_premium && (
                                        <Badge className="absolute -top-2 -right-2 bg-sunset text-white border-0 uppercase text-[10px] font-bold px-3" data-testid={`my-jv-premium-${jv.id}`}>
                                            <Star className="h-3 w-3 mr-1" /> Premium
                                        </Badge>
                                    )}
                                    <div className="flex items-center justify-between mb-2">
                                        <Badge className={`uppercase text-[10px] ${statusColors[jv.vetting_status] || ""}`}>{jv.vetting_status.replace("_", " ")}</Badge>
                                        {jv.admin_score && <span className="text-xs font-app text-muted-foreground">Score: {jv.admin_score}/100</span>}
                                    </div>
                                    <h3 className="font-display text-lg font-bold mb-1">{jv.title}</h3>
                                    <p className="text-sm text-muted-foreground font-app line-clamp-2 mb-3">{jv.project_vision}</p>
                                    <div className="flex items-center justify-between text-xs font-app">
                                        <span>{fmtFJD(jv.capital_required_fjd)}</span>
                                        <span className="text-fiji-green">{jv.roi_estimate_pct}% ROI</span>
                                    </div>
                                    {jv.admin_comments?.length > 0 && (
                                        <div className="mt-3 p-3 bg-secondary/50 rounded text-xs font-app">
                                            <div className="font-semibold mb-1">Admin notes:</div>
                                            {jv.admin_comments.slice(-2).map((c, i) => <div key={i} className="text-muted-foreground">• {c.comment}</div>)}
                                        </div>
                                    )}
                                    {jv.vetting_status === "approved" && !jv.is_premium && (
                                        <div className="mt-4 space-y-2">
                                            <div className="p-3 bg-coral/10 border border-coral/30 rounded-md">
                                                <div className="flex items-start gap-2 mb-2">
                                                    <Sparkles className="h-4 w-4 text-coral flex-shrink-0 mt-0.5" />
                                                    <div className="text-xs font-app">
                                                        <div className="font-semibold text-foreground">Premium — FJD $499 (permanent)</div>
                                                        <div className="text-muted-foreground">Pin to top + investor email blast.</div>
                                                    </div>
                                                </div>
                                                <Button onClick={() => upgradeToPremium(jv.id)} data-testid={`upgrade-premium-${jv.id}`} size="sm" className="bg-sunset text-white hover:opacity-90 w-full">
                                                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Upgrade to Premium
                                                </Button>
                                            </div>
                                            {!jv.is_boosted && (
                                                <div className="p-3 bg-fiji-green/10 border border-fiji-green/30 rounded-md">
                                                    <div className="flex items-start gap-2 mb-2">
                                                        <Sparkles className="h-4 w-4 text-fiji-green flex-shrink-0 mt-0.5" />
                                                        <div className="text-xs font-app">
                                                            <div className="font-semibold text-foreground">Boost — FJD $99 / 30 days</div>
                                                            <div className="text-muted-foreground">Top of project type for a month.</div>
                                                        </div>
                                                    </div>
                                                    <Button onClick={() => upgradeToBoost(jv.id)} data-testid={`upgrade-boost-${jv.id}`} size="sm" variant="outline" className="border-fiji-green text-fiji-green hover:bg-fiji-green hover:text-white w-full">
                                                        Boost for 30 days
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="favorites" className="mt-6 space-y-8">
                    <div>
                        <h3 className="font-display text-xl font-bold mb-3">Saved listings</h3>
                        {favs.listings.length === 0 ? <p className="text-muted-foreground font-app text-sm">No favorites yet.</p> : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{favs.listings.map((l) => <PropertyCard key={l.id} listing={l} />)}</div>
                        )}
                    </div>
                    <div>
                        <h3 className="font-display text-xl font-bold mb-3">Saved Joint Ventures</h3>
                        {favs.jvs.length === 0 ? <p className="text-muted-foreground font-app text-sm">No favorites yet.</p> : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{favs.jvs.map((j) => <JVCard key={j.id} jv={j} />)}</div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function EmptyState({ text, cta, to }) {
    return (
        <Card className="p-12 text-center bg-secondary/30">
            <p className="text-muted-foreground font-app mb-4">{text}</p>
            <Button asChild className="bg-ocean-green text-white"><Link to={to}>{cta}</Link></Button>
        </Card>
    );
}
